import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '@components/Spinner/LoadingSpinner';
import SimulationStateBadge from '@components/SimulationStateBadge/SimulationStateBadge';
import { LambdaHistoryItem as LambdaHistoryItemType } from '@api/lambdaOperationsApi';
import {
    formatLambdaHistoryDateTime,
    getLambdaParamsSummary,
    isLambdaTaskCompleted,
    isLambdaTaskRunning,
} from '../lambdaOperations.utils';
import LambdaHistoryContextMenu from './LambdaHistoryContextMenu';

type Props = {
    item: LambdaHistoryItemType;
    checked: boolean;
    layerLoading: boolean;
    restarting: boolean;
    onLayerToggle: (item: LambdaHistoryItemType, checked: boolean) => void;
    onOpenDashboard: (item: LambdaHistoryItemType) => void;
    onRestart: (item: LambdaHistoryItemType) => void;
};

const LambdaHistoryItem = ({
    item,
    checked,
    layerLoading,
    restarting,
    onLayerToggle,
    onOpenDashboard,
    onRestart,
}: Props) => {
    const { t } = useTranslation();
    const completed = isLambdaTaskCompleted(item.state);
    const canLoadLayer = completed && !!(item.lambdaLayerId || item.id);
    const paramsSummary = getLambdaParamsSummary(item.params);
    const dateTime = formatLambdaHistoryDateTime(item.updateTs, item.createTs);

    return (
        <article className="lambda-history-item" data-testid="lambdaHistoryItem">
            <div className="lambda-history-item__check">
                {layerLoading ? (
                    <LoadingSpinner size="14px" backgroundColor="transparent" />
                ) : (
                    <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canLoadLayer}
                        title={!canLoadLayer ? t('lambda-operations.layer-unavailable') : ''}
                        onChange={event => onLayerToggle(item, event.target.checked)}
                    />
                )}
            </div>
            <div className="lambda-history-item__body">
                <div className="lambda-history-item__title-line">
                    <p>{item.name || item.type || item.id}</p>
                    {isLambdaTaskRunning(item.state) ? (
                        <LoadingSpinner size="13px" backgroundColor="transparent" />
                    ) : null}
                </div>
                {paramsSummary || dateTime ? (
                    <span className="lambda-history-item__meta">
                        {[paramsSummary, dateTime].filter(Boolean).join(' - ')}
                    </span>
                ) : null}
                <SimulationStateBadge state={item.state || ''} />
            </div>
            <LambdaHistoryContextMenu
                item={item}
                restarting={restarting}
                onOpenDashboard={onOpenDashboard}
                onRestart={onRestart}
            />
        </article>
    );
};

export default LambdaHistoryItem;
